/**
 * Announcements Query Hooks — data behind "What's New".
 *
 * Two reads of the same endpoint:
 * - unread: BE-default cursor (authoritative across devices) → popup + rail dot
 * - recent: full 30-day window → /whats-new page (read + unread)
 * Unread state on the client is derived from `user.last_announcements_read_at`,
 * so mark-read never has to rewrite these caches.
 */

import { useQuery } from '@tanstack/react-query';
import { requestFlags } from '@/shared/lib/announcements/announcement-rules';
import { useAuthStore } from '@/shared/stores/auth-store';
import { announcementsClient } from '../client/announcements.client';
import { queryKeys } from '../query-keys';

const LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;

function useRequestFlags() {
  const isCreator = useAuthStore((s) => s.user?.is_creator ?? false);
  return requestFlags({ is_creator: isCreator });
}

/** Unread announcements for the current user. Fetched once per session. */
export function useUnreadAnnouncements(enabled: boolean) {
  const flags = useRequestFlags();
  return useQuery({
    queryKey: queryKeys.announcements.unread(flags),
    queryFn: () => announcementsClient.list({ flags }),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Everything published in the last 30 days (BE hard cap), newest first. */
export function useRecentAnnouncements(enabled: boolean) {
  const flags = useRequestFlags();
  return useQuery({
    queryKey: queryKeys.announcements.recent(flags),
    // `since` is computed at fetch time — a render-time clock read is a Cache
    // Components build error.
    queryFn: () =>
      announcementsClient.list({ flags, since: new Date(Date.now() - LOOKBACK_MS).toISOString() }),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}
